import { useEffect } from 'react';

const SITE_NAME = 'Yamba Africa';
const BASE_URL = 'https://yambaafrica.com';
const DEFAULT_IMAGE = `${BASE_URL}/og-image.png`;

/**
 * Imperatively injects SEO <head> tags without a HelmetProvider dependency.
 * Works with React 19 and CRA without additional peer-dep conflicts.
 *
 * @param {object} options
 * @param {string} options.title          - Page title (appended with " | Yamba Africa")
 * @param {string} options.description    - Meta description (max 160 chars)
 * @param {string} [options.image]        - OG image URL
 * @param {string} [options.url]          - Canonical URL for this page
 * @param {string} [options.type]         - OG type, default "website"
 * @param {string} [options.keywords]     - Comma-separated keywords
 * @param {object} [options.structuredData] - JSON-LD object to inject
 */
function useSEO({
  title,
  description,
  image = DEFAULT_IMAGE,
  url,
  type = 'website',
  keywords,
  structuredData,
} = {}) {
  useEffect(() => {
    const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
    const canonicalUrl = url || BASE_URL;

    // ── Title ──────────────────────────────────────────────────────────────
    document.title = fullTitle;

    // ── Helper to upsert a <meta> tag ──────────────────────────────────────
    const setMeta = (selector, attr, content) => {
      if (!content) return;
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        const [attrName] = selector.match(/\[([^\]]+)]/)?.[1].split('=') ?? [];
        if (attrName) el.setAttribute(attrName.replace(/['"]/g, ''), attr);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    // ── Helper to upsert a <link> tag ──────────────────────────────────────
    const setLink = (rel, href) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        document.head.appendChild(el);
      }
      el.setAttribute('href', href);
    };

    // Primary meta
    setMeta('meta[name="description"]', 'name', description);
    if (keywords) setMeta('meta[name="keywords"]', 'name', keywords);
    setMeta('meta[name="robots"]', 'name', 'index, follow');

    // Canonical
    setLink('canonical', canonicalUrl);

    // OG
    setMeta('meta[property="og:title"]', 'property', fullTitle);
    setMeta('meta[property="og:description"]', 'property', description);
    setMeta('meta[property="og:image"]', 'property', image);
    setMeta('meta[property="og:url"]', 'property', canonicalUrl);
    setMeta('meta[property="og:type"]', 'property', type);
    setMeta('meta[property="og:site_name"]', 'property', SITE_NAME);

    // Twitter
    setMeta('meta[name="twitter:card"]', 'name', 'summary_large_image');
    setMeta('meta[name="twitter:title"]', 'name', fullTitle);
    setMeta('meta[name="twitter:description"]', 'name', description);
    setMeta('meta[name="twitter:image"]', 'name', image);

    // ── JSON-LD structured data ────────────────────────────────────────────
    const SCRIPT_ID = 'seo-structured-data';
    let script = document.getElementById(SCRIPT_ID);
    if (structuredData) {
      if (!script) {
        script = document.createElement('script');
        script.id = SCRIPT_ID;
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(structuredData);
    } else if (script) {
      script.remove();
    }
  }, [title, description, image, url, type, keywords, structuredData]);
}

export default useSEO;
